import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Save as SaveIcon, PersonRemove as RemoveIcon, Send as InviteIcon } from '@mui/icons-material';
import { api } from '../services/api';
import { auth } from '../services/auth';
import { ClientConfig } from '../types';
import { useConfigStore } from '../stores/useConfigStore';
import { PrimaryButton } from '../components/common/PrimaryButton';
import { BillingSettings } from '../components/settings/BillingSettings';
import { ApiKeySettings } from '../components/settings/ApiKeySettings';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`account-tabpanel-${index}`}
      aria-labelledby={`account-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * My Account Page
 * General account info, Billing, and API Key management
 */
const ACCOUNT_TABS = ['profile', 'org', 'users', 'billing', 'api-key'];

export const MyAccountPage: React.FC = () => {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const activeTab = useMemo(() => {
    const idx = ACCOUNT_TABS.indexOf(tab || '');
    return idx >= 0 ? idx : 0;
  }, [tab]);
  const [config, setConfig] = useState<ClientConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Editable fields
  const [orgName, setOrgName] = useState('');
  const [industry, setIndustry] = useState('');

  // Profile state
  const [userAttributes, setUserAttributes] = useState<Record<string, string>>({});
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  // Users state
  const [users, setUsers] = useState<{ email: string; firstName: string; lastName: string; status: string; createdAt: string }[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    loadConfig();
    loadProfile();
    loadUsers();
  }, []);

  const loadProfile = async () => {
    const attrs = await auth.getUserAttributes();
    setUserAttributes(attrs);
  };

  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword) {
      setError('Please fill in all password fields');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    try {
      setPasswordSaving(true);
      setError(null);
      await auth.changePassword(oldPassword, newPassword);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordSuccess('Password changed successfully');
      setSuccessMessage('Password changed successfully');
    } catch (err: any) {
      const msg = err.message || 'Failed to change password';
      if (msg.includes('Incorrect') || msg.includes('NotAuthorizedException')) {
        setError('Current password is incorrect');
      } else if (msg.includes('Password does not conform') || msg.includes('InvalidPasswordException')) {
        setError('Password must include uppercase, lowercase, and numbers');
      } else {
        setError(msg);
      }
    } finally {
      setPasswordSaving(false);
    }
  };

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const data = await api.listUsers();
      setUsers(data);
    } catch (err: any) {
      console.error('Failed to load users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)) {
      setError('Please enter a valid email');
      return;
    }
    try {
      setInviting(true);
      setError(null);
      await api.inviteUser({
        email: inviteEmail.trim(),
        firstName: inviteFirstName.trim() || undefined,
        lastName: inviteLastName.trim() || undefined,
      });
      setInviteEmail('');
      setInviteFirstName('');
      setInviteLastName('');
      setSuccessMessage('Invitation sent! They will receive a temporary password via email.');
      await loadUsers();
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to invite user';
      setError(msg);
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveUser = async () => {
    if (!removeTarget) return;
    try {
      setRemoving(true);
      setError(null);
      await api.removeUser(removeTarget);
      setRemoveTarget(null);
      setSuccessMessage('User removed');
      await loadUsers();
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to remove user';
      setError(msg);
      setRemoveTarget(null);
    } finally {
      setRemoving(false);
    }
  };

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getClientConfig();
      setConfig(data);
      setOrgName(data.orgName || '');
      setIndustry((data as any).industry || '');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load account info');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGeneral = async () => {
    if (!config) return;
    try {
      setSaving(true);
      setError(null);
      await api.updateClientConfig({ orgName, industry });
      setSuccessMessage('Account info saved');
      useConfigStore.getState().loadConfig(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!config) {
    return <Alert severity="error">{error || 'Failed to load account'}</Alert>;
  }

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4" fontWeight={600}>
          Account
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Manage your organization, billing, and API access
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 2, pb: 1 }}>
          <Tabs
            value={activeTab}
            onChange={(_e, v) => navigate(`/account/${ACCOUNT_TABS[v]}`, { replace: true })}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                color: '#666',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '0.95rem',
                minHeight: 48,
                borderRadius: '8px',
                transition: 'all 0.2s',
                mb: 1,
                '&:hover': { bgcolor: '#f5f5f5' },
              },
              '& .Mui-selected': {
                bgcolor: '#DCF995',
                color: '#1B0826 !important',
                fontWeight: 600,
              },
              '& .MuiTabs-indicator': { display: 'none' },
            }}
          >
            <Tab label="Profile" />
            <Tab label="Org" />
            <Tab label="Users" />
            <Tab label="Billing" />
            <Tab label="API Key" />
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Profile */}
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Paper variant="outlined" sx={{ p: 3, maxWidth: 500, flex: 1, minWidth: 300 }}>
                <Typography variant="h6" fontWeight={600} mb={2}>
                  Your Profile
                </Typography>

                <TextField
                  fullWidth
                  label="Email"
                  value={userAttributes.email || ''}
                  margin="normal"
                  disabled
                  helperText="Cannot be changed"
                />
                <TextField
                  fullWidth
                  label="First Name"
                  value={userAttributes.given_name || ''}
                  margin="normal"
                  disabled
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  value={userAttributes.family_name || ''}
                  margin="normal"
                  disabled
                />
                <TextField
                  fullWidth
                  label="Organization"
                  value={userAttributes['custom:orgId'] || ''}
                  margin="normal"
                  disabled
                />
              </Paper>

              <Paper variant="outlined" sx={{ p: 3, maxWidth: 500, flex: 1, minWidth: 300 }}>
                <Typography variant="h6" fontWeight={600} mb={2}>
                  Change Password
                </Typography>

                <TextField
                  fullWidth
                  label="Current Password"
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  margin="normal"
                  autoComplete="current-password"
                />
                <TextField
                  fullWidth
                  label="New Password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  margin="normal"
                  autoComplete="new-password"
                  helperText="At least 8 characters with uppercase, lowercase, and numbers"
                />
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin="normal"
                  autoComplete="new-password"
                />

                {passwordSuccess && (
                  <Alert severity="success" sx={{ mt: 1 }} onClose={() => setPasswordSuccess(null)}>
                    {passwordSuccess}
                  </Alert>
                )}

                <PrimaryButton
                  onClick={handleChangePassword}
                  disabled={passwordSaving || !oldPassword || !newPassword || !confirmPassword}
                  sx={{ mt: 2 }}
                >
                  {passwordSaving ? 'Changing...' : 'Change Password'}
                </PrimaryButton>
              </Paper>
            </Box>
          </TabPanel>

          {/* General */}
          <TabPanel value={activeTab} index={1}>
            <Paper variant="outlined" sx={{ p: 3, maxWidth: 500 }}>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Organization Info
              </Typography>

              <TextField
                fullWidth
                label="Organization Name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Organization ID"
                value={config.orgId}
                margin="normal"
                disabled
                helperText="Cannot be changed"
              />
              <TextField
                fullWidth
                label="Industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                margin="normal"
                placeholder="e.g. E-commerce, SaaS, Logistics"
              />

              <PrimaryButton
                startIcon={<SaveIcon />}
                onClick={handleSaveGeneral}
                disabled={saving}
                sx={{ mt: 2 }}
              >
                {saving ? 'Saving...' : 'Save'}
              </PrimaryButton>
            </Paper>
          </TabPanel>

          {/* Users */}
          <TabPanel value={activeTab} index={2}>
            {/* Invite section */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Invite Team Member
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <TextField
                  label="Email"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  size="small"
                  sx={{ minWidth: 250 }}
                  placeholder="colleague@company.com"
                />
                <TextField
                  label="First Name"
                  value={inviteFirstName}
                  onChange={(e) => setInviteFirstName(e.target.value)}
                  size="small"
                  sx={{ minWidth: 150 }}
                />
                <TextField
                  label="Last Name"
                  value={inviteLastName}
                  onChange={(e) => setInviteLastName(e.target.value)}
                  size="small"
                  sx={{ minWidth: 150 }}
                />
                <PrimaryButton
                  startIcon={<InviteIcon />}
                  onClick={handleInviteUser}
                  disabled={inviting || !inviteEmail}
                >
                  {inviting ? 'Sending...' : 'Invite'}
                </PrimaryButton>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                They'll receive a temporary password via email and will be asked to set a new one on first login.
              </Typography>
            </Paper>

            {/* Users table */}
            <Paper variant="outlined">
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Joined</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usersLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No team members yet. Invite someone above.
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map((user) => {
                        const isCurrentUser = userAttributes.email === user.email;
                        const isPending = user.status === 'FORCE_CHANGE_PASSWORD';
                        return (
                          <TableRow key={user.email}>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                              {[user.firstName, user.lastName].filter(Boolean).join(' ') || '—'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={isPending ? 'Pending' : 'Active'}
                                size="small"
                                sx={{
                                  bgcolor: isPending ? 'rgba(245,158,11,0.12)' : 'rgba(34,197,94,0.12)',
                                  color: isPending ? '#f59e0b' : '#22c55e',
                                  fontWeight: 600,
                                  fontSize: '12px',
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                            </TableCell>
                            <TableCell align="right">
                              {isCurrentUser ? (
                                <Tooltip title="You can't remove yourself">
                                  <span>
                                    <IconButton disabled size="small">
                                      <RemoveIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Remove user">
                                  <IconButton
                                    size="small"
                                    onClick={() => setRemoveTarget(user.email)}
                                    sx={{ color: 'error.main' }}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Remove confirmation dialog */}
            <Dialog open={!!removeTarget} onClose={() => setRemoveTarget(null)}>
              <DialogTitle>Remove User</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Are you sure you want to remove <strong>{removeTarget}</strong> from your organization?
                  They will lose access to the dashboard immediately.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRemoveTarget(null)} disabled={removing}>
                  Cancel
                </Button>
                <Button onClick={handleRemoveUser} color="error" disabled={removing}>
                  {removing ? 'Removing...' : 'Remove'}
                </Button>
              </DialogActions>
            </Dialog>
          </TabPanel>

          {/* Billing */}
          <TabPanel value={activeTab} index={3}>
            <BillingSettings />
          </TabPanel>

          {/* API Key */}
          <TabPanel value={activeTab} index={4}>
            <ApiKeySettings config={config} onConfigUpdated={loadConfig} />
          </TabPanel>
        </Box>
      </Paper>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </Box>
  );
};
